"use client";

import type { LessonDetails } from "@/features/workspaces/types/lessonDetails"; // Import LessonDetails type

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { AlertTriangle, ArrowLeft, Loader2, OctagonAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

import { toast } from "@/utils/customToast";
import { LessonDetailsCard } from "@/features/workspaces/components/lesson-details/LessonDetailsCard";
import { LessonChatCard } from "@/features/workspaces/components/lesson-details/LessonChatCard";
import { LessonMaterialsCard } from "@/features/workspaces/components/lesson-details/LessonMaterialsCard";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { STUDENT_WORKSPACE_PATHS } from "@/routes/paths.ts";
import { cancelLesson, fetchStudentLessonDetails } from "@/features/workspaces/utils/apiUtils";
import GenericLoader from "@/components/GenericLoader.tsx";
import { ScrollArea } from "@/components/ui/scroll-area";
import { LessonActivityCard } from "@/features/workspaces/components/lesson-details/LessonActivityCard.tsx";
import { LessonParticipantsCard } from "@/features/workspaces/components/lesson-details/LessonParticipantsCard.tsx";

export const LessonDetailsPage = () => {
  const { t } = useTranslation(["workspaces", "common", "classroom", "chat"]);
  const { lessonId, workspaceId } = useParams();
  const [lessonDetails, setLessonDetails] = useState<LessonDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    fetchLessonDetails();
  }, [lessonId, workspaceId]);

  const fetchLessonDetails = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const lessonData = await fetchStudentLessonDetails(workspaceId || "", lessonId || "");

      setLessonDetails(lessonData.data);
    } catch (error: any) {
      console.error("Error fetching lesson details:", error);
      setError(t("common:errorMessage"));
      toast.error(t("common:error"), {
        description: t("common:errorMessage"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelLesson = async () => {
    if (!lessonDetails) return;

    setIsCancelling(true);
    try {
      await cancelLesson(workspaceId || "", lessonId || "");
      toast.success(t("workspaces:cancelLesson"), {
        description: t(
          "workspaces:lessonCancelledSuccess",
          "Lesson has been successfully cancelled.",
        ),
      });
      fetchLessonDetails();
    } catch (error: any) {
      console.error("Error cancelling lesson:", error);
      toast.error(t("common:error"), {
        description: t("common:errorMessage"),
      });
    } finally {
      setIsCancelling(false);
      setCancelDialogOpen(false);
    }
  };

  if (isLoading) {
    return <GenericLoader />;
  }

  if (error || !lessonDetails) {
    return (
      <div className="bg-background flex h-[calc(100dvh_-_4rem)] w-full items-center justify-center bg-gradient-to-br p-4 sm:p-6">
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="bg-card/50 w-full max-w-md rounded-2xl p-6 text-center shadow-2xl backdrop-blur-xl sm:p-8"
          initial={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.4 }}
        >
          <Icon
            className="text-destructive mx-auto mb-4"
            height={"4rem"}
            icon="mdi:alert-circle"
            width={"4rem"}
          />
          <h2 className="text-accent-foreground mb-2 text-xl font-bold sm:text-2xl">
            {t("common:error")}
          </h2>
          <p className="text-muted-foreground mb-6 text-sm sm:text-base">
            {error || t("workspaces:noLessonsFound")}
          </p>
          <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground">
            <Link to={`${STUDENT_WORKSPACE_PATHS(workspaceId || "").HOME}/lessons/listing`}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("common:backToSchedule", "Back to Schedule")}
            </Link>
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <ScrollArea className={"h-[calc(100dvh_-_4rem)]"}>
      <div className="p-3 pt-0 sm:p-4 lg:p-6">
        <div className="mx-auto max-w-[1500px]">
          {/* Main Grid Layout */}
          <div className="grid grid-cols-12 gap-3 sm:gap-4 lg:gap-6">
            {/* Main Details - Large Card */}
            <LessonDetailsCard
              lessonDetails={lessonDetails}
              setCancelDialogOpen={setCancelDialogOpen}
            />

            {/* Chat Card */}
            <div className="col-span-12 row-span-3 flex flex-col gap-3 sm:gap-4 lg:col-span-5">
              <LessonChatCard />
              <LessonParticipantsCard lessonDetails={lessonDetails} />
            </div>

            {/* Lesson Materials */}
            <LessonMaterialsCard lessonDetails={lessonDetails} />

            <LessonActivityCard lessonDetails={lessonDetails} />
          </div>
        </div>

        {lessonDetails.video_room_data?.error && lessonDetails.video_room_data.error_message && (
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="bg-destructive/10 text-destructive mt-6 flex items-center gap-3 rounded-2xl px-4 py-3 shadow-2xl backdrop-blur-xl sm:px-6 sm:py-4"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4 }}
          >
            <OctagonAlert className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm sm:text-base">{lessonDetails.video_room_data.error_message}</p>
          </motion.div>
        )}

        {/* Cancel Lesson Dialog */}
        <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <DialogContent className="bg-card text-accent-foreground border-border max-w-sm sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t("workspaces:cancelLesson")}</DialogTitle>
              <DialogDescription className="text-muted-foreground">
                {t(
                  "workspaces:cancelLessonConfirmation",
                  "Are you sure you want to cancel this lesson?",
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="bg-destructive/10 text-destructive flex items-center gap-3 rounded-xl p-3 sm:p-4">
              <AlertTriangle className="h-5 w-5 flex-shrink-0" />
              <p className="text-xs sm:text-sm">
                {t(
                  "workspaces:cancelLessonWarning",
                  "This action cannot be undone and will notify all participants.",
                )}
              </p>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                className="bg-muted hover:bg-muted/90 text-muted-foreground"
                onClick={() => setCancelDialogOpen(false)}
              >
                {t("common:cancel")}
              </Button>
              <Button
                className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                disabled={isCancelling}
                onClick={handleCancelLesson}
              >
                {isCancelling && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("workspaces:confirmCancel", "Confirm Cancel")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ScrollArea>
  );
};
